import { FC, useMemo } from 'react';
import {
  TextTitle,
  Spacer,
  LoadingInfo,
  logos,
} from '@pypestream/design-system';
import { TranslationComponent, useTranslation } from '@pypestream/translations';

// The two steps that name a "solution". Named constants so the lookup below
// matches them by VALUE — an index would silently point at the wrong step the
// first time anyone reorders the rotation.
const FOUNDATION_STEP = 'Establishing the foundation for your solution…';
const FINALIZING_STEP = 'Finalizing the setup for your solution space…';

// Only those two are translated, and that is what puts them in reach of the
// Agent vocabulary overlay (`agent-vocabulary.ts`), which can only rewrite
// strings that live in a bundle. The other eighteen name no product noun, so a
// key would buy nothing.
//
// (All twenty were plain literals at first, under a comment claiming they were
// covered by the Agent flag. They were not — hardcoded copy is invisible to the
// overlay, so the Create Agent dialog's loader still said "your solution" with
// the flag on.)
const LOADING_STEP_NAMES = [
  'Preparing your workspace framework…',
  'Setting up the initial system structure…',
  'Getting the canvas ready for building…',
  FOUNDATION_STEP,
  'Aligning core components behind the scenes…',
  'Configuring the environment for your workflows…',
  'Organizing the elements that support your workspace…',
  'Syncing essential system processes…',
  'Creating the base layout for your project…',
  'Initializing key platform services…',
  'Preparing the space where your workflows will live…',
  'Confirming everything is ready for your first steps…',
  'Structuring the environment for smooth orchestration…',
  `Loading the tools you'll need to start creating…`,
  'Setting up integration points for future connections…',
  'Bringing foundational logic online…',
  'Stabilizing the workspace for editing…',
  'Getting your environment ready to grow…',
  FINALIZING_STEP,
  'Your workspace will be ready in a moment.',
];

export const StepLoading: FC<{ active: boolean }> = ({ active = false }) => {
  const [t] = useTranslation();

  // Memoised on `t`, whose identity changes when the language — or the Agent
  // vocabulary overlay — changes. `LoadingInfo` restarts its rotation whenever
  // the `steps` property changes reference, so a fresh array on every render
  // would keep resetting the loader back to step one.
  const steps = useMemo(
    () =>
      LOADING_STEP_NAMES.map((step) => {
        if (step === FOUNDATION_STEP) {
          return t('pro-studio/homepage:vocabulary.loading_foundation', {
            defaultValue: step,
          });
        }
        if (step === FINALIZING_STEP) {
          return t('pro-studio/homepage:vocabulary.loading_finalizing', {
            defaultValue: step,
          });
        }
        return step;
      }),
    [t]
  );

  return (
    <div>
      <TextTitle size="small" textAlign="center">
        <TranslationComponent i18nKey="pro-studio/homepage:vocabulary.loading_title">
          Creating Your Solution
        </TranslationComponent>
      </TextTitle>
      <Spacer size="xlarge" />
      <LoadingInfo paused={!active} steps={steps} icon={logos.proStudio} />
    </div>
  );
};
